import { Injectable } from '@angular/core';
import { logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { Unsubscribe } from 'firebase/firestore';
import { User } from 'firebase/auth';

import { environment } from 'src/environments/environment';
import { FirebaseService } from '../firebase/firebase.service';

declare global {
  interface Window {
    trackJs: any;
  }
}

// Replaces the legacy Segment client. The public API (`logPage`, `logEvent`)
// is unchanged so callers don't need updating. Firebase Analytics is the
// single platform across all Crewdle products.
@Injectable({
  providedIn: 'root',
})
export class AnalyticEventService {
  userSub: Unsubscribe | null = null;
  orgSub: Unsubscribe | null = null;

  constructor(private firebase: FirebaseService) {
    void this.firebase.analytics.then(() => {
      if (window.trackJs) {
        window.trackJs.configure({ version: environment.version });
      }
    });
    this.getAuthUser();
  }

  public logPage(): void {
    void this.track('page_view');
  }

  public logEvent(eventType: string): void {
    void this.track(eventType);
  }

  private async track(eventName: string, params: Record<string, unknown> = {}): Promise<void> {
    try {
      const analytics = await this.firebase.analytics;
      if (!analytics) return;
      logEvent(analytics, eventName, params);
    } catch {
      // Analytics must never break a flow.
    }
  }

  private async resetUser(): Promise<void> {
    try {
      const analytics = await this.firebase.analytics;
      if (analytics) setUserId(analytics, null);
    } catch {
      // ignore
    }
    if (this.userSub) {
      this.userSub();
      this.userSub = null;
    }
    if (this.orgSub) {
      this.orgSub();
      this.orgSub = null;
    }
  }

  private async logUser(id: string, email: string, name: string, createdAt: string): Promise<void> {
    try {
      const analytics = await this.firebase.analytics;
      if (!analytics) return;
      setUserId(analytics, id);
      setUserProperties(analytics, { email, name, created_at: createdAt });
    } catch {
      // ignore
    }
  }

  // Firebase Analytics has no first-class "group" / company concept like
  // Segment. Store the org as user properties so it can be used as a filter
  // dimension on every event.
  private async logCompany(id: string, name: string): Promise<void> {
    try {
      const analytics = await this.firebase.analytics;
      if (!analytics) return;
      setUserProperties(analytics, { org_id: id, org_name: name });
    } catch {
      // ignore
    }
  }

  private getAuthUser(): void {
    this.firebase.auth.onAuthStateChanged((authUser) => {
      if (authUser && !authUser.isAnonymous) {
        this.getUserData(authUser);
      } else {
        void this.resetUser();
      }
    });
  }

  private getUserData(authUser: User): void {
    if (this.userSub) {
      this.userSub();
      this.userSub = null;
    }
    this.userSub = this.firebase.firestore.docListen(`users/${authUser.uid}`, (userDoc) => {
      if (userDoc.exists()) {
        const user: any = userDoc.data();
        if (user) {
          const createdAt = new Date(authUser.metadata.creationTime).toISOString();
          void this.logUser(authUser.uid, user.email, user.name, createdAt);
          if (user.organization) {
            this.getOrgData(user.organization);
          } else if (this.orgSub) {
            this.orgSub();
            this.orgSub = null;
          }
        }
      }
    });
  }

  private getOrgData(orgId: string) {
    if (this.orgSub) {
      this.orgSub();
      this.orgSub = null;
    }
    this.orgSub = this.firebase.firestore.docListen(`organizations/${orgId}`, (orgDoc) => {
      if (orgDoc.exists()) {
        const org: any = orgDoc.data();
        void this.logCompany(orgId, org.name);
      }
    });
  }
}
